import { memo, useCallback } from 'react';
import { AppBar, IconButton, Stack, Toolbar, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { StyleObj } from '../../../../@types';
import HeaderTabNavigation from './HeaderTabNavigation';
import AuthButtons from '../../../molecules/AuthButtons';
import SearchBar from '../../../molecules/SearchBar';
import { GOLA_HEADER_HEIGHT } from '../../../../config/skinRegistry/configs';
import AccountMenu from './AccountMenu';
import Logo from '../../../icons/Logo';
import useProductAvailability from '../../../../hooks/useProductAvailability';

const styles: StyleObj = {
  appBar: {
    height: { xs: 'fit-content', md: GOLA_HEADER_HEIGHT },
    backgroundColor: 'primary.600',
  },
  toolbar: {
    width: '100%',
    minHeight: { xs: GOLA_HEADER_HEIGHT, md: GOLA_HEADER_HEIGHT },
    height: { xs: GOLA_HEADER_HEIGHT, md: GOLA_HEADER_HEIGHT },
    justifyContent: 'space-between',
    pr: 1,
    pl: 0.5,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuIcon: {
    color: 'info.main',
    fontSize: 15,
    mr: 0.5,
  },
  navLink: {
    color: 'neutral.25',
    textDecoration: 'none',
    typography: 'body2',
    fontWeight: 500,
    '&:hover': {
      color: 'secondary.main',
    },
  },
  balanceContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    lineHeight: 1,
  },
  balanceText: {
    fontSize: 9,
    fontWeight: 500,
    lineHeight: 1,
  },
  accountIconBtn: {
    p: 0,
    mb: 0.5,
  },
  accountIcon: {
    color: 'info.main',
    fontSize: 24,
  },
};

type HeaderProps = {
  isDrawerOpen?: boolean;
  isSidebarCompact?: boolean;
  showSidebarCollapse?: boolean;
  toggleDrawerVisibility?: (isOpen: boolean) => void;
  toggleSidebarCompact?: () => void;
};

const Header = memo(({ toggleDrawerVisibility }: HeaderProps) => {
  const { isAuthenticated } = useAuth();
  const { isCasinoOnlyProduct } = useProductAvailability();

  const openDrawer = useCallback(() => {
    toggleDrawerVisibility?.(true);
  }, [toggleDrawerVisibility]);

  return (
    <AppBar position='fixed' elevation={0} sx={styles.appBar}>
      <Toolbar sx={styles.toolbar}>
        <SearchBar />
        <Stack sx={styles.leftSection}>
          {isCasinoOnlyProduct ? (
            <Logo size='small' isClickable={false} />
          ) : (
            <IconButton onClick={openDrawer} size='small'>
              <MenuIcon sx={styles.menuIcon} />
              <Typography sx={styles.navLink}>A-Z</Typography>
            </IconButton>
          )}
        </Stack>

        <HeaderTabNavigation />

        {isAuthenticated ? (
          <Stack sx={styles.balanceContainer}>
            <AccountMenu />
          </Stack>
        ) : (
          <AuthButtons />
        )}
      </Toolbar>
    </AppBar>
  );
});

export default Header;
